import { DOCUMENT } from '@angular/common';
import { DestroyRef, inject, Injectable, InjectionToken } from '@angular/core';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
import { FetchLike } from '@environments/runtime-config';
import { exhaustMap, interval, Subscription } from 'rxjs';
import { NewVersionNotice } from './new-version-notice/new-version-notice';

/** Intervalle entre deux vérifications (RG-version-nouvelle-version-detectee). */
export const UPDATE_CHECK_INTERVAL_MS = 5 * 60 * 1000;

/** La balise de `index.html` qui porte l'identifiant de la construction chargée. */
export const APP_BUILD_META = 'app-build';

/** Le fichier, servi à côté de `index.html`, qui porte l'identifiant de la construction servie. */
export const VERSION_FILE = 'version.json';

/**
 * `fetch` plutôt que `HttpClient` : la lecture n'a rien à faire des intercepteurs, qui
 * ajouteraient un jeton et montreraient une erreur à l'utilisateur pour une vérification
 * qu'il ignore, et qui doit échouer en silence.
 */
export const VERSION_FETCH = new InjectionToken<FetchLike>('VERSION_FETCH', {
  providedIn: 'root',
  factory: () => (url, init) => fetch(url, init),
});

export const PAGE_RELOAD = new InjectionToken<() => void>('PAGE_RELOAD', {
  providedIn: 'root',
  factory: () => {
    const document = inject(DOCUMENT);
    return () => document.location.reload();
  },
});

/**
 * Prévient l'utilisateur qu'une nouvelle version a été déployée pendant que la page était
 * ouverte (cadrage 2026-028).
 *
 * Sans service worker : `index.html` est servi sans cache, et un rechargement charge toujours
 * la version servie. Il suffit donc de comparer l'identifiant de la construction chargée, écrit
 * dans la page, à celui que le serveur sert désormais — puis de laisser l'utilisateur recharger
 * quand sa saisie le permet.
 */
@Injectable({ providedIn: 'root' })
export class AppUpdateService {
  private readonly document = inject(DOCUMENT);
  private readonly snackBar = inject(MatSnackBar);
  private readonly fetchFn = inject(VERSION_FETCH);
  private readonly reload = inject(PAGE_RELOAD);
  private polling?: Subscription;
  private notice?: MatSnackBarRef<NewVersionNotice>;

  constructor() {
    inject(DestroyRef).onDestroy(() => this.polling?.unsubscribe());
  }

  /**
   * Lance la vérification périodique. Sans effet quand la page ne porte pas d'identifiant :
   * sous `ng serve` et dans les tests, rien n'a été construit à comparer.
   */
  start(): void {
    const loadedBuild = this.loadedBuild();
    if (!loadedBuild || this.polling) {
      return;
    }

    this.polling = interval(UPDATE_CHECK_INTERVAL_MS)
      .pipe(exhaustMap(() => this.servedBuild()))
      .subscribe((servedBuild) => {
        // Une différence dans un sens ou dans l'autre : revenir en arrière change aussi ce
        // que l'API attend de l'application.
        if (servedBuild !== undefined && servedBuild !== loadedBuild) {
          this.announce();
        }
      });
  }

  private loadedBuild(): string | undefined {
    const meta = this.document.querySelector<HTMLMetaElement>(`meta[name="${APP_BUILD_META}"]`);
    return meta?.content || undefined;
  }

  /** L'identifiant servi, ou `undefined` quand la lecture échoue : on ne conclut alors à rien. */
  private async servedBuild(): Promise<string | undefined> {
    try {
      // Résolu contre `<base href>` : relatif à la route courante, il tomberait sur le repli
      // de la SPA.
      const url = new URL(VERSION_FILE, this.document.baseURI).href;
      const response = await this.fetchFn(url, { cache: 'no-store' });
      if (!response.ok) {
        return undefined;
      }
      const body: unknown = await response.json();
      const build = (body as { build?: unknown } | null)?.build;
      return typeof build === 'string' && build !== '' ? build : undefined;
    } catch {
      return undefined;
    }
  }

  /**
   * Un seul message à la fois. Il ne se ferme jamais de lui-même (`duration: 0`) ; fermé par
   * « Plus tard », ou remplacé par un autre message de l'application, il revient à la
   * vérification suivante (RG-version-avertissement-jusqu-au-rechargement).
   */
  private announce(): void {
    if (this.notice) {
      return;
    }
    this.notice = this.snackBar.openFromComponent(NewVersionNotice, {
      duration: 0,
      horizontalPosition: 'right',
      verticalPosition: 'top',
    });
    this.notice.afterDismissed().subscribe(({ dismissedByAction }) => {
      this.notice = undefined;
      if (dismissedByAction) {
        this.reload();
      }
    });
  }
}
